'use client';

import { useTranslations } from 'next-intl';
import { FileText, FileCheck, FilePen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DynamicForm } from '../types';

interface DynamicFormMetricsProps {
  forms: DynamicForm[];
}

export function DynamicFormMetrics({ forms }: DynamicFormMetricsProps) {
  const t = useTranslations('dynamicForms');

  const total = forms.length;
  const active = forms.filter((f) => f.status === 'active').length;
  const drafts = forms.filter((f) => f.status === 'draft').length;

  const hasDrafts = drafts > 0;

  const metrics = [
    {
      label: t('totalForms'),
      value: total,
      icon: FileText,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950'
    },
    {
      label: t('activeForms'),
      value: active,
      icon: FileCheck,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-950'
    }
  ];

  return (
    <div className='grid gap-4 sm:grid-cols-3'>
      {metrics.map((metric) => (
        <Card key={metric.label}>
          <CardContent className='flex items-center gap-4 px-5'>
            <div className='min-w-0 flex-1'>
              <p className='text-muted-foreground text-sm font-medium'>
                {metric.label}
              </p>
              <p className='text-2xl font-bold'>
                {metric.value.toLocaleString()}
              </p>
            </div>
            <div className={cn('rounded-xl p-3', metric.bgColor)}>
              <metric.icon className={cn('h-5 w-5', metric.color)} />
            </div>
          </CardContent>
        </Card>
      ))}
      <Card>
        <CardContent className='flex items-center gap-4 px-5'>
          <div className='min-w-0 flex-1'>
            <p
              className={cn(
                'text-sm font-medium',
                hasDrafts
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-muted-foreground'
              )}
            >
              {t('draftForms')}
            </p>
            <p
              className={cn(
                'text-2xl font-bold',
                hasDrafts
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              )}
            >
              {drafts.toLocaleString()}
            </p>
          </div>
          <div
            className={cn(
              'rounded-xl p-3',
              hasDrafts ? 'bg-amber-100 dark:bg-amber-900' : 'bg-muted'
            )}
          >
            <FilePen
              className={cn(
                'h-5 w-5',
                hasDrafts
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              )}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
